import axios, { AxiosError, type AxiosResponse, type InternalAxiosRequestConfig } from 'axios';
import { z } from 'zod';
import useAppStore from '@/stores/appStore';
import { useAuth0 } from '@auth0/auth0-react';
import { useSessionExpiryStore } from '@/stores/sessionExpiryStore';
import { isV1Request, v1ResponseError } from './v1';
import { requiresReauthentication } from '@/utils/authErrors';

// Response header carrying the backend session's remaining idle seconds.
// Exposed via Access-Control-Expose-Headers, so readable cross-origin.
const SESSION_EXPIRES_HEADER = 'session-expires-in';

// Define response type matching ACTUAL API structure
// Success: { code: 0, status: 200, data: {...} }
// Error:   { code: <number>, status: <non-200>, message: "error message" }
export interface ApiResponse<T = unknown> {
  code: number;
  status: number;
  data?: T;
  message?: string;
}

// Zod schema for API responses - matches actual structure
export const ApiResponseSchema = z.object({
  code: z.number(),
  status: z.number(),
  data: z.unknown().optional(),
  message: z.string().optional(),
});

// Custom error class for API errors
export class ApiError extends Error {
  code: number;
  status: number;
  
  constructor(message: string, status: number, code: number) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
  }
}

// Create axios instance with default config
export const axiosClient = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Store for Auth0 token getter function - updated by useAxios hook
let tokenGetter: ((options?: { cacheMode?: 'off' | 'cache-only' }) => Promise<string>) | null = null;

// Dedup flag: prevents multiple 401 handlers from firing simultaneously
let isHandling401 = false;

// Custom event name for session expiry — MainLayout listens for this
export const SESSION_EXPIRED_EVENT = 'auth:session-expired';

// Backend inner codes on a 401 (HTTP status stays 401; the code disambiguates).
// Other auth failures have their own codes — we default those to the recovery
// ladder like an unknown 401.
export const AUTH_INNER_CODE = {
  INVALID_TOKEN: 1000, // Auth0 token invalid/broken — a fresh token / re-login may fix it
  SESSION_EXPIRED: 1001, // backend idle session died — nothing the client does can revive it
} as const;

// Why the session ended — carried on SESSION_EXPIRED_EVENT so the UI can vary copy.
// 'idle' is dispatched by the flow editor's UI-idle timeout (not by a 401).
export type SessionExpiredReason = 'session_expired' | 'invalid_token' | 'idle' | 'unknown';

export interface SessionExpiredDetail {
  reason: SessionExpiredReason;
  /** Silent renewal is unavailable, so teardown must not try to obtain another token. */
  tokenUnavailable?: boolean;
}

// Emit session-expired event (MainLayout will handle proper Auth0 logout)
const handleSessionExpired = (
  reason: SessionExpiredReason = 'unknown',
  tokenUnavailable = false,
) => {
  if (isHandling401) return; // Already handling a 401
  isHandling401 = true;

  console.warn(`[Auth] Session ended (${reason}) — triggering logout`);
  window.dispatchEvent(
    new CustomEvent<SessionExpiredDetail>(SESSION_EXPIRED_EVENT, {
      detail: { reason, tokenUnavailable },
    }),
  );

  // Reset flag after a delay to allow retry if user logs back in
  setTimeout(() => { isHandling401 = false; }, 5000);
};

// Token refresh with retry logic using Auth0's cache control
const getTokenWithRefresh = async (): Promise<string | null> => {
  // Capture the getter up front. Logging out nulls `tokenGetter`, and the
  // await below yields — so re-reading the module-level binding in the catch
  // can find it null and throw "tokenGetter is not a function".
  const getToken = tokenGetter;
  if (!getToken) return null;

  try {
    // First attempt: Get token from cache (default behavior)
    const token = await getToken();
    return token;
  } catch (error) {
    // If logout nulled the getter while we were awaiting, the session is being
    // torn down on purpose — don't try to refresh it back.
    if (!tokenGetter) return null;

    // The SDK already attempts renewal when the access token expires. Missing
    // or rejected refresh credentials cannot be repaired by bypassing its cache.
    if (requiresReauthentication(error)) {
      handleSessionExpired('invalid_token', true);
      return null;
    }

    console.warn('[Auth] Cached token fetch failed, attempting refresh:', error);

    try {
      // Second attempt: Force refresh by bypassing Auth0 cache
      const refreshedToken = await getToken({ cacheMode: 'off' });
      console.info('[Auth] Token refreshed successfully');
      return refreshedToken;
    } catch (refreshError) {
      console.error('[Auth] Token refresh failed completely:', refreshError);
      if (!tokenGetter) return null;
      if (requiresReauthentication(refreshError)) {
        handleSessionExpired('invalid_token', true);
      }
      return null;
    }
  }
};

// Marker fields attached to a request config so 401 handling can be precise:
// only a 401 on a request that ACTUALLY carried a token is a real session expiry.
type AuthRequestConfig = InternalAxiosRequestConfig & {
  __hadAuthToken?: boolean;
  __isRetry?: boolean;
  __resyncAttempted?: boolean;
};

// The auth endpoints themselves must never trigger a session resync: /login/ IS
// the resync, and a 401 from either of them means the token is truly rejected.
const isAuthEndpoint = (url?: string) =>
  !!url && (url.includes('/login') || url.includes('/logout') || url.includes('/register'));

// In-flight resync, shared so a burst of parallel 401s (every mounted query
// fails at once) produces exactly one /login/ call.
let resyncInFlight: Promise<boolean> | null = null;

// Re-introduce the current Auth0 token to the backend by re-running the
// /login/ handshake. The backend only recognises tokens it has seen via
// /login/ — after a re-login, Auth0 mints a NEW token and data queries race
// ahead of MainLayout's handshake, so the first responses come back
// "invalid token" even though the token is perfectly good. Registering it and
// retrying beats logging the user straight back out. This cannot resurrect a
// genuinely dead session: /login/ itself rejects a bad token, and that 401
// still expires the session below.
const resyncBackendSession = (): Promise<boolean> => {
  if (!tokenGetter) return Promise.resolve(false);
  if (!resyncInFlight) {
    resyncInFlight = (async () => {
      try {
        console.warn('[Auth] Backend rejected the token — re-running /login/ handshake');
        // The request interceptor attaches the current token; the response
        // interceptor treats a /login/ 401 as a real expiry (see isAuthEndpoint).
        const response = await axiosClient.post('/login');
        return response.status !== 204;
      } catch {
        return false;
      } finally {
        resyncInFlight = null;
      }
    })();
  }
  return resyncInFlight;
};

// Retry a 401'd request once after forcing a fresh token. Returns the new
// response on success, or null if a retry isn't possible / also failed.
const retryWithFreshToken = async (
  config: AuthRequestConfig,
): Promise<AxiosResponse | null> => {
  if (!tokenGetter || config.__isRetry) return null;
  try {
    // Force Auth0 to mint a fresh token (updates its cache)
    const fresh = await tokenGetter({ cacheMode: 'off' });
    if (!fresh) return null;
    // Re-issue; the request interceptor will attach the now-fresh cached token
    return await axiosClient.request({ ...config, __isRetry: true } as AuthRequestConfig);
  } catch (error) {
    if (!tokenGetter) return null;
    if (requiresReauthentication(error)) {
      handleSessionExpired('invalid_token', true);
    }
    return null;
  }
};

// Full recovery ladder for a 401 on a request that carried a token:
// fresh token → /login/ resync → give up and expire the session.
// `innerCode` is the backend's disambiguating code (see AUTH_INNER_CODE).
// Returns the successful retried response, or null after expiry was triggered.
const recoverFrom401 = async (
  cfg: AuthRequestConfig,
  innerCode: number,
): Promise<AxiosResponse | null> => {
  // A genuinely idle-expired session (1001) must NOT be recovered: refreshing the
  // token or re-running /login/ would silently revive a session the backend
  // deliberately expired, defeating the whole idle-logout UX. Expire immediately.
  if (innerCode === AUTH_INNER_CODE.SESSION_EXPIRED) {
    handleSessionExpired('session_expired');
    return null;
  }

  // Rung 1: force renewal using the tenant's configured Auth0 capabilities.
  const retried = await retryWithFreshToken(cfg);
  if (retried) return retried;
  if (useAppStore.getState().session.isLoggingOut) return null;

  // Rung 2: the token may simply be unknown to the backend (fresh login,
  // races the MainLayout /login/ handshake). Introduce it and re-issue once.
  if (!cfg.__resyncAttempted && !isAuthEndpoint(cfg.url)) {
    const registered = await resyncBackendSession();
    if (registered) {
      try {
        return await axiosClient.request({
          ...cfg,
          __resyncAttempted: true,
        } as AuthRequestConfig);
      } catch {
        // The retry's own interceptor pass has already handled its 401
        // (deduped below); fall through to expiry.
      }
    }
  }

  if (useAppStore.getState().registrationRequired) return null;

  handleSessionExpired(
    innerCode === AUTH_INNER_CODE.INVALID_TOKEN ? 'invalid_token' : 'unknown',
  );
  return null;
};

// Request interceptor for adding auth token with automatic refresh
axiosClient.interceptors.request.use(
  async (config: AuthRequestConfig) => {
    if (tokenGetter) {
      try {
        const token = await getTokenWithRefresh();
        if (token && config.headers) {
          config.headers.Authorization = `Bearer ${token}`;
          // Mark that this request genuinely carried a valid token
          config.__hadAuthToken = true;
        } else if (!token) {
          // We believe we're authenticated but couldn't obtain a token (e.g. Auth0
          // is still rehydrating right after login). Do NOT send a token-less
          // request that the backend would answer with a session-killing 401.
          // Terminal Auth0 failures have already triggered expiry above. For
          // transient failures, keep the session and allow a later retry.
          console.warn('[Auth] No token available — aborting request locally:', config.url);
          return Promise.reject(new ApiError('Authentication not ready', 0, 0));
        }
      } catch (error) {
        console.error('[Auth] Unexpected error in token logic:', error);
        return Promise.reject(new ApiError('Authentication not ready', 0, 0));
      }
    }
    return config;
  },
  (error) => Promise.reject(error),
);

// Response interceptor - TWO-LAYER ERROR CHECKING
axiosClient.interceptors.response.use(
  async (response) => {
    // Every authenticated response refreshes the idle countdown — activity extends
    // the session server-side, and this header reports the new remaining seconds.
    useSessionExpiryStore.getState().setFromHeader(response.headers?.[SESSION_EXPIRES_HEADER]);

    if (response.status === 204 && response.config.url?.replace(/\/$/, '') === '/login') {
      useAppStore.getState().setBackendUserId(null);
      useAppStore.getState().setRegistrationRequired(true);
      return response;
    }

    const data = response.data;

    // V1 order.status is a business state such as PENDING, not an HTTP status.
    if (isV1Request(response.config.url)) return response;

    // LAYER 1: Check if response has expected structure
    if (typeof data === 'object' && data !== null && 'status' in data) {
      const apiResponse = data as ApiResponse;
      const innerStatus = apiResponse.status;
      const innerCode = apiResponse.code;
      const message = apiResponse.message;

      // LAYER 2: Check INNER status code (not HTTP status)
      if (innerStatus !== 200) {
        const errorMessage = message || `API Error (status: ${innerStatus}, code: ${innerCode})`;
        console.error(`[API] Error: status=${innerStatus}, code=${innerCode}, message=${message}`);

        // Handle 401 (deduped). Only treat it as a real session expiry when the
        // request actually carried a token — a token-less 401 (e.g. auth not yet
        // ready right after login) must NOT log the user out.
        if (innerStatus === 401) {
          const cfg = response.config as AuthRequestConfig;
          if (cfg.__hadAuthToken) {
            const recovered = await recoverFrom401(cfg, innerCode);
            if (recovered) return recovered;
          }
          return Promise.reject(new ApiError(errorMessage, innerStatus, innerCode));
        }

        throw new ApiError(errorMessage, innerStatus, innerCode);
      }
    }

    return response;
  },
  async (error: AxiosError) => {
    if (axios.isCancel(error)) return Promise.reject(error);
    // Even error responses carry the idle countdown — keep it fresh (a 401 below
    // will clear it via the expiry flow anyway).
    useSessionExpiryStore.getState().setFromHeader(
      error.response?.headers?.[SESSION_EXPIRES_HEADER] as string | undefined,
    );

    if (isV1Request(error.config?.url)) {
      const status = error.response?.status ?? 0;
      if (status === 401) {
        const cfg = error.config as AuthRequestConfig;
        if (cfg.__hadAuthToken) {
          const recovered = await recoverFrom401(cfg, 0);
          if (recovered) return recovered;
        }
      }
      return Promise.reject(v1ResponseError(status, error.response?.data, error.response?.headers?.['retry-after']));
    }

    // Handle actual HTTP errors (network failures, server crashes, etc.)
    const responseData = error.response?.data as ApiResponse | undefined;
    const message = responseData?.message || error.message || 'Network error';
    const status = responseData?.status || error.response?.status || 0;
    const code = responseData?.code || 0;

    console.error(`[HTTP] Error: status=${status}, code=${code}, message=${message}`);

    // Handle HTTP 401 (deduped). Same rule as above: only a 401 on a request that
    // genuinely carried a token is a session expiry; retry once with a fresh token first.
    if (status === 401) {
      const cfg = error.config as AuthRequestConfig | undefined;
      if (cfg?.__hadAuthToken) {
        const recovered = await recoverFrom401(cfg, code);
        if (recovered) return recovered;
      }
      return Promise.reject(new ApiError(message, status, code));
    }

    return Promise.reject(new ApiError(message, status, code));
  },
);

/**
 * Hook to get a configured axios instance
 * - Updates the token getter function for authenticated requests
 * - Must be called early in app lifecycle (e.g., MainLayout)
 */
export const useAxios = () => {
  const { getAccessTokenSilently, isAuthenticated } = useAuth0();

  // Update the token getter when user is authenticated
  // Auth0's getAccessTokenSilently accepts options for cache control
  if (isAuthenticated) {
    tokenGetter = (options) => getAccessTokenSilently(options);
  } else {
    tokenGetter = null;
  }

  return { axiosClient };
};

/**
 * Keep-alive: extend the backend idle session on demand (the "Stay signed in"
 * action). Any authenticated request extends the window; /login/ is the canonical
 * session handshake and its response refreshes the `Session-Expires-In` header
 * (which the interceptor writes into the session-expiry store). Uses a bare
 * request so it doesn't trigger the login UI/toast flow. A dead session 401s here
 * and the normal expiry flow takes over.
 */
export const pingSession = async (): Promise<void> => {
  const response = await axiosClient.post('/login');
  const seconds = Number(response.headers?.[SESSION_EXPIRES_HEADER]);
  if (!Number.isFinite(seconds) || seconds <= 0) {
    // Login succeeded, but the renewed expiry is unknown. Do not expire the
    // renewed session using its old deadline; subsequent headers restore it.
    useSessionExpiryStore.getState().clear();
  }
};

// Export the axiosClient instance directly
export default axiosClient;
